import React from "react";
import {
  MdHourglassEmpty,
  MdSync,
} from "react-icons/md";
import styles from "./Queue.module.css";
import SearchBar from "../../components/SearchBar/SearchBar";
import QueueItem from "../../components/QueueItem/QueueItem";
import ZoomModal from "../../components/ZoomModal/ZoomModal";
import InternalPagination from "../../components/InternalPagination/InternalPagination";
import FilterOptions from "../../components/FilterOptions/FilterOptions";
import useQueue, { getFilterIcon, STATUS_FILTERS } from "../../customHook/Queue/useQueue";
import MobileZoomModal from "../../components/MobileZoomModal/MobileZoomModal";

const Queue = () => {
  const {
    searchTerm,
    setSearchTerm,
    activeFilter,
    handleFilterChange,
    queueFilters,
    filterCount,
    isLoading,
    error,
    hasVisibleItems,
    visibleItems,
    activeItems,
    historyItems,
    pagination,
    zoomModal,
    closeZoomModal,
    getQueueItemProps,
  } = useQueue();

  const { page, pageSize, totalPages, total, setPage, setPageSize } = pagination || {};

  return (
    <div className="app-page flex-1 flex flex-col min-h-0 min-w-0">
      <div className="px-6 pt-5 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-headline-md text-[20px] text-primary mb-0.5 font-bold tracking-tight">Queue</h2>
          <p className="font-body-md text-[10px] text-secondary">Track active generations and review previous results.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SearchBar
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search generations..."
          />
          <FilterOptions
            filters={queueFilters}
            onFilterChange={handleFilterChange}
          />
        </div>
      </div>

      <div className="flex-1 px-6 pt-1 pb-20 overflow-y-auto min-h-0">
        {/* Stats cards grid */}
        <section className={styles.statsGrid}>
          {["completed", "processing", "queued", "failed"].map((key) => {
            const filter = STATUS_FILTERS.find((f) => f.key === key);
            if (!filter) return null;
            const Icon = getFilterIcon(filter.key);
            const cardClass = styles[`statCard${key.charAt(0).toUpperCase() + key.slice(1)}`] || "";
            const iconClass = styles[`statIcon${key.charAt(0).toUpperCase() + key.slice(1)}`] || "";
            const valueClass = styles[`statValue${key.charAt(0).toUpperCase() + key.slice(1)}`] || "";
            return (
              <div
                key={filter.key}
                className={`${styles.statCard} ${cardClass}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${iconClass}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className={styles.statLabel}>{filter.label}</span>
                  </div>
                </div>
                <span className={`${styles.statValue} ${valueClass}`}>{filterCount(filter.key)}</span>
              </div>
            );
          })}
        </section>

        {isLoading && (
          <div className="flex-1 flex flex-col items-center justify-center py-20 w-full text-center">
            <MdSync className="text-[36px] text-slate-400 animate-spin mb-2" />
            <p className="text-sm font-semibold text-secondary">Loading queue...</p>
          </div>
        )}
        {error && <div className={`${styles.queueState} ${styles.errorState}`}>{error}</div>}

        {!isLoading && !error && !hasVisibleItems && (
          <div className={styles.emptyState}>
            <MdHourglassEmpty className="text-[36px] text-slate-300 mb-2" />
            <p>No generations found</p>
          </div>
        )}

        {!isLoading && !error && hasVisibleItems && (
          <div className={styles.queueDetailsSection}>
            {activeFilter === "all" ? (
              <>
                {activeItems.length > 0 && (
                  <section className={styles.queueGroup}>
                    <h3 className={styles.groupHeading}>Active ({activeItems.length})</h3>
                    <div className={styles.queueList}>
                      {activeItems.map((item, index) => (
                        <QueueItem {...getQueueItemProps(item, index)} />
                      ))}
                    </div>
                  </section>
                )}

                {historyItems.length > 0 && (
                  <section className={styles.queueGroup}>
                    <h3 className={styles.groupHeading}>History ({historyItems.length})</h3>
                    <div className={styles.queueList}>
                      {historyItems.map((item, index) => (
                        <QueueItem {...getQueueItemProps(item, index)} />
                      ))}
                    </div>
                  </section>
                )}
              </>
            ) : (
              <section className={styles.queueGroup}>
                <h3 className={styles.groupHeading}>
                  {activeFilter
                    .split(",")
                    .map((k) => STATUS_FILTERS.find((filter) => filter.key === k)?.label || k)
                    .join(", ")}{" "}
                  ({visibleItems.length})
                </h3>
                <div className={styles.queueList}>
                  {visibleItems.map((item, index) => (
                    <QueueItem {...getQueueItemProps(item, index)} />
                  ))}
                </div>
              </section>
            )}

            <footer
              id="action-footer"
              className="app-footer-bar fixed bottom-0 md:left-56 left-0 right-0 px-6 h-12 flex items-center justify-between z-50 transition-all duration-300"
            >
              <InternalPagination
                page={page}
                limit={pageSize}
                total={total}
                totalPages={totalPages}
                limitOptions={[10, 20, 30]}
                onPageChange={setPage}
                onLimitChange={setPageSize}
              />
            </footer>
          </div>
        )}
      </div>

      <ZoomModal
        modalId="queueZoomModal"
        front={zoomModal.front}
        back={zoomModal.back}
        onClose={closeZoomModal}
        showFlip={zoomModal.showFlip}
        isBackground={zoomModal.isBackground}
      />

      <MobileZoomModal
        isOpen={zoomModal.isMobileOpen}
        onClose={closeZoomModal}
        front={zoomModal.front}
        back={zoomModal.back}
        showFlip={zoomModal.showFlip}
      />
    </div>
  );
};

export default Queue;
